/**
 * @file The glyph a file shows for its type. Consumers: the attachments panel
 * file rows, communication log rows, and the files inside an expanded log entry.
 *
 * It resolves the type itself from the filename and the declared content type
 * rather than taking a resolved type, so every surface reaches the one rule in
 * `resolveAttachmentFileType` and none can classify a file its own way. The
 * glyphs are statically imported from `@ant-design/icons` — routing them
 * through the lazy `Icon` atom would open a boundary per row — and each is a
 * module-level element because the accessible name is fixed by the type, not by
 * the file. Nothing sets a colour: the icon inherits `currentColor` so it never
 * competes with the label colours beside it, which carry the real signal.
 */

import { type FC, type ReactNode } from 'react';
import {
    BankOutlined,
    CompassOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileUnknownOutlined,
    FileWordOutlined,
    MailOutlined,
    TableOutlined,
} from '@ant-design/icons';
import type { AttachmentFileType } from './index.types';
import { resolveAttachmentFileType } from './resolveAttachmentFileType';

const GLYPHS: Record<AttachmentFileType, ReactNode> = {
    pdf: <FilePdfOutlined aria-label="PDF file" />,
    word: <FileWordOutlined aria-label="Word file" />,
    spreadsheet: <FileExcelOutlined aria-label="Spreadsheet file" />,
    csv: <TableOutlined aria-label="CSV file" />,
    email: <MailOutlined aria-label="Email file" />,
    cad: <CompassOutlined aria-label="CAD file" />,
    bim: <BankOutlined aria-label="BIM file" />,
    image: <FileImageOutlined aria-label="Image file" />,
    text: <FileTextOutlined aria-label="Text file" />,
    unknown: <FileUnknownOutlined aria-label="File of an unrecognised type" />,
};

export const AttachmentFileTypeIcon: FC<{
    fileName: string | undefined;
    declaredContentType?: string | undefined;
}> = ({ fileName, declaredContentType }) => GLYPHS[resolveAttachmentFileType(fileName, declaredContentType)];
